import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'

import { mergeReducedMotionPreset, useThemeMotion } from '../../animation/themeMotion'

type Props = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  angular?: boolean
  zClass?: string
}

/**
 * 底部操作表：与 {@link ThemeModal} 共用 theme-modal-backdrop 与主题动效曲线。
 */
export function ThemedBottomSheet({
  open,
  onClose,
  title,
  children,
  angular = false,
  zClass = 'z-[240]',
}: Props) {
  const prefersReduced = useReducedMotion()
  const motionPreset = useThemeMotion()
  const preset = mergeReducedMotionPreset(motionPreset, !!prefersReduced)
  const overlayDur = Math.min(0.32, Math.max(0.2, preset.duration * 0.55))
  const panelDur = Math.min(0.42, Math.max(0.24, preset.expandDuration * 0.72))
  const ease = [...preset.easing] as [number, number, number, number]

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (typeof document === 'undefined') return null

  const reduced = !!prefersReduced
  const backdropTransition = reduced
    ? { duration: 0.16, ease: [0.4, 0, 1, 1] as [number, number, number, number] }
    : { duration: Math.min(0.24, overlayDur), ease }
  const panelTransition = reduced
    ? { duration: 0.18, ease: [0.4, 0, 1, 1] as [number, number, number, number] }
    : { duration: panelDur, ease }
  const panelHidden = reduced ? { opacity: 0 } : { opacity: 0, y: '100%' }
  const panelVisible = { opacity: 1, y: 0 }
  const round = angular ? 'rounded-none' : 'rounded-t-2xl'

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className={['fixed inset-0 flex flex-col justify-end', zClass].join(' ')} role="presentation">
          <motion.button
            type="button"
            className="theme-modal-backdrop absolute inset-0 cursor-default"
            aria-label="关闭"
            initial={{ opacity: 0.88 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={backdropTransition}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={panelHidden}
            animate={panelVisible}
            exit={panelHidden}
            transition={panelTransition}
            style={{ transformOrigin: 'bottom center' }}
            className={[
              'relative border-t border-line bg-panel px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2',
              'shadow-[0_-8px_32px_color-mix(in_srgb,var(--ink)_28%,transparent)]',
              round,
            ].join(' ')}
          >
            {children}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
